import { useEffect, useId, useRef, useState, type ReactNode } from 'react';
import { EllipsisVertical } from 'lucide-react';
import { useOverlay } from './overlay.js';
import { t } from '../i18n/index.js';

export type MenuItem = {
  label: string;
  onSelect: () => void;
  icon?: ReactNode;
  danger?: boolean;
  disabled?: boolean;
};

/**
 * Row actions behind a "⋯" button: click or Enter opens, arrows move, Escape closes, an outside
 * click dismisses. Items are plain buttons so the whole menu is keyboard reachable.
 */
export function Menu({ items, label }: { items: MenuItem[]; label?: string }) {
  const [open, setOpen] = useState(false);
  const wrap = useRef<HTMLSpanElement>(null);
  const id = useId();
  useOverlay(open);

  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setOpen(false);
    };
    const onClick = (e: MouseEvent) => {
      if (!wrap.current?.contains(e.target as Node)) setOpen(false);
    };
    document.addEventListener('keydown', onKey);
    document.addEventListener('mousedown', onClick);
    return () => {
      document.removeEventListener('keydown', onKey);
      document.removeEventListener('mousedown', onClick);
    };
  }, [open]);

  const move = (from: number, delta: number) => {
    const buttons = Array.from(
      wrap.current?.querySelectorAll<HTMLButtonElement>('.popover > button:not([disabled])') ?? [],
    );
    if (buttons.length === 0) return;
    const next = (from + delta + buttons.length) % buttons.length;
    buttons[next]?.focus();
  };

  return (
    <span className="menu-wrap" ref={wrap}>
      <button
        type="button"
        className="icon-btn small"
        aria-haspopup="menu"
        aria-expanded={open}
        aria-controls={open ? id : undefined}
        aria-label={label ?? t('actions')}
        title={label ?? t('actions')}
        onClick={() => setOpen((o) => !o)}
      >
        <EllipsisVertical size={16} aria-hidden="true" />
      </button>
      {open && (
        <div className="popover" role="menu" id={id} aria-label={label ?? t('actions')}>
          {items.map((it, i) => (
            <button
              key={it.label}
              type="button"
              role="menuitem"
              className={it.danger ? 'danger-item' : ''}
              disabled={it.disabled}
              onClick={() => {
                setOpen(false);
                it.onSelect();
              }}
              onKeyDown={(e) => {
                if (e.key === 'ArrowDown') {
                  e.preventDefault();
                  move(i, 1);
                } else if (e.key === 'ArrowUp') {
                  e.preventDefault();
                  move(i, -1);
                }
              }}
            >
              {it.icon}
              {it.label}
            </button>
          ))}
        </div>
      )}
    </span>
  );
}
